import { Box, Button, Chip, Link as MuiLink, Typography } from "@mui/material"
import type { Licence } from "@xop/dashboard-shared"
import { Eye, KeyRound } from "lucide-react"
import { useCallback, type JSX } from "react"
import { Link as RouterLink } from "react-router-dom"

import { ResellerNetworkColumn } from "@crm-src/components/reseller-network-column"
import { useResellerPagedList } from "@crm-src/hooks/useResellerPagedList"
import { LicenceStatusChip, LicenceWorkflowChip } from "@crm-src/pages/licences/licence-chips"
import { fetchLicences } from "@crm-src/pages/licences/licences-api"
import { formatLicenceDate as formatDate, licenceActorName as who } from "@crm-src/pages/licences/licence-provenance"

type LicenceRow = Omit<Licence, "id"> & { id: string; licenceId: number }

/** Defence in depth: keep only licences that ARE this reseller's — its
 * customer's reseller, or created by it. crm-api's `reseller_id` filter already
 * decides this; the check here means a crm-api that predates the filter (and so
 * ignores it, answering with the actor's WHOLE scope) still cannot put another
 * company's licences on this profile during a rolling deploy. */
function belongsTo(licence: Licence, resellerId: number): boolean {
  return licence.reseller?.id === resellerId || licence.createdBy?.reseller?.id === resellerId
}

function matches(licence: Licence, search: string): boolean {
  if (!search) return true
  const needle = search.toLowerCase()
  return [licence.customerName, licence.label, licence.hardwareAddress, licence.currentIssue?.serial ?? ""]
    .some((field) => field.toLowerCase().includes(needle))
}

/**
 * Licences on the reseller profile (2026-09-15) — what this company's
 * customers are licensed for, which of those it created and issued itself, and
 * for which customer.
 *
 * READS `GET /api/licences?resellerId=` — the dashboard BFF, gated on
 * `crm:licences`, never crm-api directly (the licence pages' rule; see
 * docs/licensing.md → Permissions). crm-api narrows the actor's walled scope to
 * licences for this reseller's customers OR created by it. The tab itself is
 * filtered out of the profile without the CRM `licences:access` page grant.
 *
 * WHO. `createdBy` / `currentIssue.issuedBy` say "reseller" (named) or "XOP".
 * They were first recorded on 2026-09-15, so an older licence reads as XOP —
 * the issue's email label is still shown, which is the only record there is.
 *
 * The endpoint returns the whole scope in one read, so search and paging are
 * done here, through the same `useResellerPagedList` + `ResellerNetworkColumn`
 * pair every other list on the profile uses.
 */
export function ResellerLicencesTab({ resellerId }: { resellerId: string }): JSX.Element {
  const list = useResellerPagedList<LicenceRow>(
    useCallback(
      async ({ page, perPage, search }) => {
        const all = (await fetchLicences({ resellerId }))
          .filter((l) => belongsTo(l, Number(resellerId)))
          .filter((l) => matches(l, search))
        const start = (page - 1) * perPage
        return {
          rows: all.slice(start, start + perPage).map((l) => ({ ...l, id: String(l.id), licenceId: l.id })),
          totalCount: all.length,
          totalPages: Math.max(1, Math.ceil(all.length / perPage)),
        }
      },
      [resellerId],
    ),
  )

  return (
    <Box data-cy="reseller-licences-tab">
      <ResellerNetworkColumn<LicenceRow>
        data-cy="reseller-licences"
        title="Licences for this reseller"
        icon={<KeyRound size={18} />}
        entityNoun="licences"
        emptyMessage="No licences yet — neither for this reseller's customers nor created by it"
        list={list}
        renderRow={(licence) => {
          const issue = licence.currentIssue
          const createdBy = who(licence.createdBy)
          const issuedBy = issue ? who(issue.issuedBy) : null
          const issuedOn = formatDate(issue?.issuedAt)
          const expires = licence.expiresOn ? formatDate(licence.expiresOn) : null

          return (
            <Box
              data-cy="reseller-licence"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                p: 1.5,
                bgcolor: "surfaceSoft",
                borderRadius: 1,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1, display: "grid", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    <MuiLink component={RouterLink} to={`/crm/customers/${licence.customerId}`} data-cy="reseller-licence-customer">
                      {licence.customerName || `Customer #${licence.customerId}`}
                    </MuiLink>
                  </Typography>
                  <LicenceStatusChip licence={licence} />
                  <LicenceWorkflowChip licence={licence} />
                </Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {licence.label ? `${licence.label} · ` : ""}
                  <Box component="span" sx={{ fontFamily: "ui-monospace, monospace" }}>{licence.hardwareAddress}</Box>
                  {" · "}
                  {expires ? `expires ${expires}` : "perpetual"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, fontSize: 12, color: "text.secondary" }}>
                  {createdBy && (
                    <Chip
                      size="small"
                      variant="outlined"
                      data-cy="reseller-licence-created-by"
                      color={licence.createdBy?.kind === "reseller" ? "secondary" : "default"}
                      label={`Created by ${createdBy}`}
                    />
                  )}
                  <Box component="span" data-cy="reseller-licence-issue">
                    {issue
                      ? `Issued ${issue.serial}${issuedOn ? ` on ${issuedOn}` : ""}${issuedBy ? ` by ${issuedBy}` : ""}${issue.issuedByLabel ? ` (${issue.issuedByLabel})` : ""}`
                      : "Not issued yet"}
                  </Box>
                </Box>
              </Box>
              <Button
                component={RouterLink}
                to={`/crm/licences/${licence.licenceId}`}
                size="small"
                variant="outlined"
                startIcon={<Eye size={14} />}
                data-cy="reseller-licence-view"
                aria-label={`View licence for ${licence.customerName}${licence.label ? ` · ${licence.label}` : ""}`}
                sx={{ flexShrink: 0 }}
              >
                View
              </Button>
            </Box>
          )
        }}
      />
    </Box>
  )
}
